import { apiClient } from './api';
import { isApiSuccess, isApiError } from '@/types';
import {
  GetAnnouncementsRequest,
  GetAnnouncementsResponse,
  GetPublicAnnouncementsRequest,
  GetPublicAnnouncementsResponse,
  GetAnnouncementResponse,
  CreateAnnouncementRequest,
  CreateAnnouncementResponse,
  UpdateAnnouncementRequest,
  UpdateAnnouncementResponse,
  PublishAnnouncementResponse,
  AddInteractionRequest,
  AddInteractionResponse,
  AnnouncementWithInteractions,
  convertAnnouncementDates,
} from '@/types/announcements';

/**
 * Announcements API client with exact backend contract matching
 * 
 * CRITICAL: These methods must match backend handler responses exactly
 * Backend returns: { success: true, data: {...}, message: "..." }
 */
export class AnnouncementsApi {
  /**
   * Get announcements visible to authenticated user
   * Matches: GET /announcements handler
   */
  async getAnnouncements(params: GetAnnouncementsRequest = {}): Promise<GetAnnouncementsResponse> {
    const queryParams = new URLSearchParams();
    
    // Convert request parameters to query string (matches backend parameter parsing)
    if (params.type) queryParams.append('type', params.type);
    if (params.priority) {
      if (Array.isArray(params.priority)) {
        queryParams.append('priority', params.priority.join(','));
      } else {
        queryParams.append('priority', params.priority);
      }
    }
    if (params.visibility) {
      if (Array.isArray(params.visibility)) {
        queryParams.append('visibility', params.visibility.join(','));
      } else {
        queryParams.append('visibility', params.visibility);
      }
    }
    if (params.status) {
      if (Array.isArray(params.status)) {
        queryParams.append('status', params.status.join(','));
      } else {
        queryParams.append('status', params.status);
      }
    }
    if (params.isPinned !== undefined) queryParams.append('isPinned', params.isPinned.toString());
    if (params.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.orderBy) queryParams.append('orderBy', params.orderBy);
    if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection);

    const url = `/announcements${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get(url);

    if (isApiError(response.data)) {
      const errorMsg = typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error?.message;
      throw new Error(errorMsg || 'Request failed');
    }

    if (!isApiSuccess<GetAnnouncementsResponse>(response.data)) {
      throw new Error('Invalid response format from server');
    }

    // Backend returns ISO date strings, convert for consistent handling
    const data = response.data.data!;
    return {
      announcements: data.announcements.map(convertAnnouncementDates),
      pagination: data.pagination
    };
  }

  /**
   * Get public announcements (no authentication required)
   * Matches: GET /announcements/public handler
   */
  async getPublicAnnouncements(params: GetPublicAnnouncementsRequest = {}): Promise<GetPublicAnnouncementsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.featured !== undefined) queryParams.append('featured', params.featured.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const url = `/announcements/public${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get(url);

    if (isApiError(response.data)) {
      const errorMsg = typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error?.message;
      throw new Error(errorMsg || 'Request failed');
    }

    if (!isApiSuccess<GetPublicAnnouncementsResponse>(response.data)) {
      throw new Error('Invalid response format from server');
    }

    const data = response.data.data!;
    return {
      announcements: data.announcements.map(convertAnnouncementDates),
      pagination: data.pagination
    };
  }

  /**
   * Get specific announcement by ID
   * Matches: GET /announcements/{id} handler
   */
  async getAnnouncement(id: string): Promise<AnnouncementWithInteractions> {
    const response = await apiClient.get(`/announcements/${id}`);

    if (isApiError(response.data)) {
      const errorMsg = typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error?.message;
      throw new Error(errorMsg || 'Request failed');
    }

    if (!isApiSuccess<GetAnnouncementResponse>(response.data)) {
      throw new Error('Invalid response format from server');
    }

    return convertAnnouncementDates(response.data.data!.announcement);
  }

  /**
   * Create new announcement (admin/organizer only)
   * Matches: POST /announcements handler
   */
  async createAnnouncement(data: CreateAnnouncementRequest): Promise<AnnouncementWithInteractions> {
    const response = await apiClient.post('/announcements', data);

    if (isApiError(response.data)) {
      const errorMsg = typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error?.message;
      throw new Error(errorMsg || 'Request failed');
    }

    if (!isApiSuccess<CreateAnnouncementResponse>(response.data)) {
      throw new Error('Invalid response format from server');
    }

    return convertAnnouncementDates(response.data.data!.announcement);
  }

  /**
   * Update existing announcement
   * Matches: PUT /announcements/{id} handler
   */
  async updateAnnouncement(id: string, data: UpdateAnnouncementRequest): Promise<AnnouncementWithInteractions> {
    const response = await apiClient.put(`/announcements/${id}`, data);

    if (isApiError(response.data)) {
      const errorMsg = typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error?.message;
      throw new Error(errorMsg || 'Request failed');
    }

    if (!isApiSuccess<UpdateAnnouncementResponse>(response.data)) {
      throw new Error('Invalid response format from server');
    }

    return convertAnnouncementDates(response.data.data!.announcement);
  }

  /**
   * Publish announcement
   * Matches: POST /announcements/{id}/publish handler
   */
  async publishAnnouncement(id: string): Promise<AnnouncementWithInteractions> {
    const response = await apiClient.post(`/announcements/${id}/publish`);

    if (isApiError(response.data)) {
      const errorMsg = typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error?.message;
      throw new Error(errorMsg || 'Request failed');
    }

    if (!isApiSuccess<PublishAnnouncementResponse>(response.data)) {
      throw new Error('Invalid response format from server');
    }

    return convertAnnouncementDates(response.data.data!.announcement);
  }

  /**
   * Add user interaction with announcement
   * Matches: POST /announcements/{id}/interactions handler
   */
  async addInteraction(id: string, interaction: AddInteractionRequest): Promise<{ 
    announcement: AnnouncementWithInteractions;
    interaction: { type: string; added: boolean };
  }> {
    const response = await apiClient.post(`/announcements/${id}/interactions`, interaction);

    if (isApiError(response.data)) {
      const errorMsg = typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error?.message;
      throw new Error(errorMsg || 'Request failed');
    }

    if (!isApiSuccess<AddInteractionResponse>(response.data)) {
      throw new Error('Invalid response format from server');
    }

    const data = response.data.data!;
    return {
      announcement: convertAnnouncementDates(data.announcement),
      interaction: data.interaction
    };
  }

  /**
   * Remove user interaction with announcement
   * Note: Backend doesn't have a dedicated endpoint, so we'll use a DELETE method on interactions
   */
  async removeInteraction(id: string, interactionType: string): Promise<void> {
    // This would need to be implemented in backend if needed
    // For now, we'll use the same endpoint with a different approach
    await apiClient.delete(`/announcements/${id}/interactions/${interactionType}`);
  }

  /**
   * Delete announcement (admin/author only)
   * Note: This endpoint would need to be added to backend if not already present
   */
  async deleteAnnouncement(id: string): Promise<void> {
    const response = await apiClient.delete(`/announcements/${id}`);

    if (isApiError(response.data)) {
      const errorMsg = typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error?.message;
      throw new Error(errorMsg || 'Request failed');
    }
  }
}

// Export singleton instance
export const announcementsApi = new AnnouncementsApi();